import React from "react";
import { Link, useNavigate } from "react-router-dom";
import siigmaticLogo from "../assets/logos/Siigmatic_BK.webp";
import introImage from "../assets/intro.webp";
import windowIcon from "../assets/icons/window.webp";
import energyReportIcon from "../assets/icons/energy-report.webp";
import webIcon from "../assets/icons/web.webp";
import tickIcon from "../assets/icons/tick.webp";
import alLogo from "../assets/brands/al-windows.webp";
import asaLogo from "../assets/brands/asa-windows.webp";
import epicviewLogo from "../assets/brands/evu.webp";
import jlLogo from "../assets/brands/jl-windows.webp";
import stegbarLogo from "../assets/brands/stegbar.webp";
import trendLogo from "../assets/brands/trend.webp";

const featureCards = [
    {
        title: "Unlock Full Access",
        bullets: [
            "Compare All Brands In One Place",
            "Save & Manage Projects",
            "Instant Compliance Results",
            "Export Reports",
        ],
    },
    {
        title: "Full Platform Access",
        badge: "$189 / month",
        note: "(billed annually)",
        bullets: [
            "Company Wide Access",
            "Multi Brand Comparison",
            "Project Management",
        ],
        cta: "Get Paid Access",
        finePrint: "* 12 months prepaid payment required.",
    },
];

const steps = [
    {
        title: "Drag & Drop Energy Report",
        icon: energyReportIcon,
        maxWidthPx: 240,
    },
    {
        title: "View Compliant Systems",
        icon: webIcon,
        maxWidthPx: 100,
    },
    {
        title: "Select & Proceed",
        icon: tickIcon,
        maxWidthPx: 100,
    },
];

const sponsors = [
    {
        name: "Stegbar",
        logo: stegbarLogo,
    },
    {
        name: "JL Windows and Doors Pty Ltd",
        logo: jlLogo,
    },
    {
        name: "A&L Windows",
        logo: alLogo,
    },
    {
        name: "Trend",
        logo: trendLogo,
    },
    {
        name: "EpicView Windows Pty Ltd",
        logo: epicviewLogo,
    },
    {
        name: "Asa Windows",
        logo: asaLogo,
    },
];

// email
const subject = encodeURIComponent("SIIGMATIC Hub Access Request");
const body = encodeURIComponent("Hi Team,\n\n This is [Your Name] from [Your Company]. I am interested in gaining paid access to the SIIGMATIC Hub. Please let me know the next steps to proceed.\n\nThank you!");
const email_href = `mailto:sales@ciilock.com?subject=${subject}&body=${body}`;

const sponsorLink = (brand) => `/sponsor/${encodeURIComponent(brand)}`;

const Home = () => {
    const navigate = useNavigate();

    const containerClass = "w-full max-w-[1100px] mx-8 md:mx-16";
    const baseCardClass =
        "rounded-[2rem] border border-[rgba(61,61,61,0.1)] bg-[rgba(170,170,170,0.08)] shadow-[0_2px_2px_rgba(0,0,0,0.1)]";

    return (
        <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: '"DM Sans", sans-serif' }}>
            <section className="flex justify-center">
                <div className={containerClass}>
                    <header className="my-4 flex items-center">
                        <div className="w-[200px]">
                            <img src={siigmaticLogo} alt="SIIGMATIC logo" className="h-auto w-full object-contain" />
                        </div>
                    </header>
                </div>
            </section>

            <div className="h-px w-screen bg-[rgba(0,0,0,0.2)]" />

            <section className="mt-8 flex justify-center">
                <div className={`${containerClass} space-y-3 text-left`}>
                    <h1 className="text-[2.25em] font-semibold leading-tight">Welcome to SIIGMATIC Hub.</h1>
                    <p className="text-base leading-[1.5] text-slate-800">
                        Your one-stop destination for compliant window systems and a wide range of tools from
                        renowned sponsored brands. Here, you'll discover innovative solutions designed to meet
                        industry standards and enhance your projects. Whether you're a professional installer or a
                        DIY enthusiast, our curated selection ensures you have access to the latest products and
                        resources to achieve exceptional results. Explore our offerings and elevate your window
                        solutions with SIIGMATIC Hub!
                    </p>
                </div>
            </section>

            <section className="mt-8 flex justify-center">
                <div className={containerClass}>
                    <div
                        className="relative z-[1] mb-16 flex min-h-[500px] flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl p-8 text-center text-white shadow-[0_4px_6px_rgba(0,0,0,0.1)]"
                        style={{
                            backgroundImage: `url(${introImage})`,
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "cover",
                        }}
                    >
                        <div className="absolute inset-0 z-[-1] rounded-3xl bg-black/20" />
                        <div className="w-[100px]">
                            <img src={windowIcon} alt="window" className="h-auto w-full object-contain" />
                        </div>
                        <h2 className="text-[2em] font-medium leading-tight">Find Compliant Window Systems</h2>
                        <p className="text-base leading-[1.5]">
                            Compare across brands, meet energy requirements, and reduce redesign time.
                        </p>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <button
                                type="button"
                                onClick={() => navigate("/signin")}
                                className="rounded-lg bg-[#007BFF] px-8 py-3 text-base font-medium text-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors duration-300 hover:bg-[#0056b3]"
                            >
                                Login
                            </button>
                            <a
                                href={email_href}
                                className="rounded-lg border-2 border-white bg-transparent px-8 py-3 text-base font-medium text-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors duration-300 hover:bg-white hover:text-[#007BFF]"
                            >
                                Get Paid Access
                            </a>
                        </div>
                        <p className="text-base leading-[1.5]">For Fabricators, Builders & Designers.</p>
                    </div>
                </div>
            </section>

            <div className="h-px w-screen bg-[rgba(0,0,0,0.2)]" />

            <section className="mt-8 flex justify-center">
                <div className={`${containerClass} mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2`}>
                    {featureCards.map((card) => (
                        <div key={card.title} className={`${baseCardClass} flex flex-col gap-4 p-8 text-left`}>
                            <h3 className="text-[1.25em] font-medium leading-tight">{card.title}</h3>
                            <div className="h-px w-full bg-[rgba(0,0,0,0.2)]" />

                            {card.badge ? (
                                <div className="flex flex-wrap items-center gap-3 text-[#0053ac]">
                                    <h2 className="text-[2.3em] font-semibold leading-tight">{card.badge}</h2>
                                    <h3 className="mt-[0.4rem] text-[1.25em] font-medium leading-tight">{card.note}</h3>
                                </div>
                            ) : null}

                            <ul className="list-none space-y-1 p-0 text-base leading-[1.5]">
                                {card.bullets.map((bullet) => (
                                    <li key={bullet}>&#10003; {bullet}</li>
                                ))}
                            </ul>

                            {card.cta ? (
                                <>
                                    <a
                                        href={email_href}
                                        className="inline-flex w-full items-center justify-center text-center rounded-lg bg-[#007BFF] px-8 py-3 text-base font-medium text-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors duration-300 hover:bg-[#0056b3]"
                                    >
                                        {card.cta}
                                    </a>
                                    <small className="text-[rgb(122,122,122)]">
                                        <i>{card.finePrint}</i>
                                    </small>
                                </>
                            ) : null}
                        </div>
                    ))}
                </div>
            </section>

            <div className="h-px w-screen bg-[rgba(0,0,0,0.2)]" />

            <section className="mt-8 flex justify-center">
                <div className={`${containerClass} mb-8 flex flex-col text-left`}>
                    <div>
                        <h1 className="text-[2.25em] font-semibold leading-tight">How It Works</h1>
                        <p className="text-base leading-[1.5]">
                            Access brand-specific tools to find compliant systems using their products.
                        </p>
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
                        {steps.map((step, index) => (
                            <div key={step.title}>
                                <div className="mb-4 flex items-center">
                                    <div className="mr-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0053ac] text-[1.25em] font-medium text-white">
                                        {index + 1}
                                    </div>
                                    <h3 className="text-[1.25em] font-medium leading-tight">{step.title}</h3>
                                </div>
                                <div className={`${baseCardClass} flex min-h-[280px] items-center justify-center`}>
                                    <div style={{ width: `${step.maxWidthPx}px` }}>
                                        <img src={step.icon} alt={step.title} className="h-auto w-full" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="h-px w-screen bg-[rgba(0,0,0,0.2)]" />

            <section className="mt-8 flex justify-center">
                <div className={`${containerClass} mb-8 text-left`}>
                    <div>
                        <h1 className="text-[2.25em] font-semibold leading-tight">Free Tools By Sponsored Brands</h1>
                        <p className="text-base leading-[1.5]">
                            Access Brand-Specific Tools To Find Compliant Systems Using Their Products
                        </p>
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {sponsors.map((brand) => (
                            <div key={brand.name} className={`${baseCardClass} flex flex-col items-center gap-4 p-8`}>
                                <div className="max-w-[200px]">
                                    <img
                                        src={brand.logo}
                                        alt={`${brand.name} logo`}
                                        className="h-auto w-full object-contain"
                                    />
                                </div>
                                <h3 className="text-center text-[1.25em] font-medium leading-tight min-h-14">{brand.name}</h3>
                                <Link
                                    to={sponsorLink(brand.name)}
                                    className="inline-flex rounded-lg bg-[#007BFF] px-8 py-3 text-base font-medium text-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors duration-300 hover:bg-[#0056b3]"
                                >
                                    Free Access
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mt-2 flex justify-center bg-white">
                <div className={`${containerClass} mb-16 mt-8`}>
                    <div className="text-center">
                        <h1 className="text-[2.25em] font-semibold leading-tight text-black">
                            Compare across all brands in one place
                        </h1>
                        <p className="text-base leading-[1.5] text-black">Stop switching between tools.</p>
                        <a
                            href={email_href}
                            className="mt-4 inline-flex rounded-lg bg-[#007BFF] px-8 py-3 text-base font-medium text-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors duration-300 hover:bg-[#0056b3]"
                        >
                            Upgrade To Full Access
                        </a>
                    </div>
                </div>
            </section>

            <div className="h-px w-screen bg-[rgba(0,0,0,0.2)]" />

            <footer className="flex justify-center py-8 md:py-16">
                <div className={`${containerClass} flex flex-col items-center justify-between gap-4 md:flex-row`}>
                    <div className="w-[200px]">
                        <img src={siigmaticLogo} alt="SIIGMATIC logo" className="h-auto w-full object-contain" />
                    </div>
                    <div className="text-base leading-[1.5] text-slate-700">© {new Date().getFullYear()} CiiLOCK Engineering Pty Ltd.</div>
                </div>
            </footer>
        </div>
    );
};

export default Home;
